import {
  applyDragMove,
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  type DragMoveEvent,
} from '@risksmart-app/atomic-ui';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ZodType } from 'zod';

import {
  findRuleReferencesToFields,
  getDisallowedCycleCandidates,
  pruneRuleReferencesToFields,
  wouldCreateCycleForContainers,
} from './conditionalLogicGraph';
import {
  buildConditionalLogicFieldOptions,
  createFieldEditorSchema,
  createSectionEditorSchema,
  type ExternalFieldMeta,
  type FieldEditorValues,
  isPersistedCustomField,
  type SectionEditorValues,
} from './config';
import {
  buildFieldDefaults,
  buildFieldSummary,
  buildSectionDefaults,
  DEFAULT_LANG,
  type FieldSummary,
} from './constants';
import {
  DeleteImpactConfirmationDialog,
  type DeletionImpact,
} from './parts/DeleteImpactConfirmationDialog';
import { ExclusiveSwitchGroup } from './parts/ExclusiveSwitchGroup';
import { FieldEditorConfigFields } from './parts/FieldEditorConfigFields';
import { FormEditorMainDialog } from './parts/FormEditorMainDialog';
import { FormTextField } from './parts/FormFields';
import { SubEditorDialog } from './parts/SubEditorDialog';
import type {
  ConditionalLogicOption,
  FormEditorDialogProps,
  FormEditorOutput,
} from './types';
import { useFormEditorDialogStore } from './useFormEditorDialogStore';

export type {
  ConditionalLogicOption,
  FieldTypeConditionMetadata,
  FieldTypeOption,
  FormEditorDialogLang,
  FormEditorDialogProps,
  FormEditorFieldData,
  FormEditorInitialData,
  FormEditorOutput,
  FormEditorPreviewData,
  FormEditorSectionData,
  FormPreviewHandle,
} from './types';

type ActiveView = 'form-editor' | 'section-builder' | 'field-editor';

const FormEditorDialog = ({
  open,
  onOpenChange,
  initialData,
  getValueOptions,
  fieldOptions: externalFieldOptions,
  getFieldTypeMetadata,
  onSave,
  saving,
  lang,
  renderPreview,
  fieldTypeOptions,
}: FormEditorDialogProps) => {
  const [activeView, setActiveView] = useState<ActiveView>('form-editor');
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [addingFieldToSectionId, setAddingFieldToSectionId] = useState<
    string | null
  >(null);

  // Internal data state
  const [containerOrder, setContainerOrder] = useState<string[]>([]);
  const [containers, setContainers] = useState<Record<string, string[]>>({});
  const [fields, setFields] = useState<Record<string, FieldSummary>>({});
  const [sectionNames, setSectionNames] = useState<Record<string, string>>({});
  const [sectionConfigs, setSectionConfigs] = useState<
    Record<string, SectionEditorValues>
  >({});
  const [fieldConfigs, setFieldConfigs] = useState<
    Record<string, FieldEditorValues>
  >({});
  const [disallowedSectionId, setDisallowedSectionId] = useState<string | null>(
    null
  );

  // Snapshot of initial state for dirty checking
  const initialSnapshotRef = useRef<{
    containerOrder: string[];
    containers: Record<string, string[]>;
    fields: Record<string, FieldSummary>;
    sectionNames: Record<string, string>;
    sectionConfigs: Record<string, SectionEditorValues>;
    fieldConfigs: Record<string, FieldEditorValues>;
  } | null>(null);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) {
      return false;
    }

    return !isEqual(initialSnapshotRef.current, {
      containerOrder,
      containers,
      fields,
      sectionNames,
      sectionConfigs,
      fieldConfigs,
    });
  }, [
    containerOrder,
    containers,
    fields,
    sectionNames,
    sectionConfigs,
    fieldConfigs,
  ]);

  // Snapshot of open accordion sections before navigating to a sub-editor,
  // so we can restore the same state when the user returns.
  const savedOpenSectionsRef = useRef<string[] | null>(null);

  const navigateToSubEditor = useCallback((view: ActiveView) => {
    savedOpenSectionsRef.current =
      useFormEditorDialogStore.getState().openSections;
    setActiveView(view);
  }, []);

  const returnToFormEditor = useCallback(() => {
    if (savedOpenSectionsRef.current) {
      useFormEditorDialogStore.setState({
        openSections: savedOpenSectionsRef.current,
      });
      savedOpenSectionsRef.current = null;
    }
    setActiveView('form-editor');
  }, []);

  const nextIdRef = useRef(0);
  const generateId = useCallback((prefix: string) => {
    nextIdRef.current += 1;

    return `${prefix}-${Date.now()}-${nextIdRef.current}`;
  }, []);

  // Initialize from initialData when dialog opens
  useEffect(() => {
    if (open) {
      const data = initialData ?? { sections: [], fields: {} };
      const newContainerOrder = data.sections.map((s) => s.id);
      const newContainers = Object.fromEntries(
        data.sections.map((s) => [s.id, s.fieldIds])
      );
      const newSectionNames = Object.fromEntries(
        data.sections.map((s) => [s.id, s.name])
      );
      const newSectionConfigs = Object.fromEntries(
        data.sections.filter((s) => s.config).map((s) => [s.id, s.config!])
      );
      const newFields = Object.fromEntries(
        Object.entries(data.fields).map(([id, f]) => [id, buildFieldSummary(f)])
      );
      const newFieldConfigs = Object.fromEntries(
        Object.entries(data.fields)
          .filter(([, f]) => f.config)
          .map(([id, f]) => [id, f.config!])
      );

      setContainerOrder(newContainerOrder);
      setContainers(newContainers);
      setSectionNames(newSectionNames);
      setSectionConfigs(newSectionConfigs);
      setFields(newFields);
      setFieldConfigs(newFieldConfigs);

      // Capture snapshot for dirty checking
      initialSnapshotRef.current = {
        containerOrder: newContainerOrder,
        containers: newContainers,
        fields: newFields,
        sectionNames: newSectionNames,
        sectionConfigs: newSectionConfigs,
        fieldConfigs: newFieldConfigs,
      };

      setActiveView('form-editor');
      setEditingSectionId(null);
      setEditingFieldId(null);
      setAddingFieldToSectionId(null);
      setDisallowedSectionId(null);
      savedOpenSectionsRef.current = null;
      useFormEditorDialogStore.setState({
        openSections: data.sections.map((s) => s.id),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleMove = useCallback(
    (event: DragMoveEvent) =>
      setContainers(
        (prev) => applyDragMove(prev, event) as Record<string, string[]>
      ),
    []
  );

  const canMove = useCallback(
    (event: DragMoveEvent) => {
      const tentative = applyDragMove(containers, event) as Record<
        string,
        string[]
      >;
      const allowed = !wouldCreateCycleForContainers({
        fieldConfigs,
        sectionConfigs,
        containers: tentative,
      });
      setDisallowedSectionId(allowed ? null : String(event.toContainerId));

      return allowed;
    },
    [containers, fieldConfigs, sectionConfigs]
  );

  const handleDragEnd = useCallback(() => setDisallowedSectionId(null), []);
  const handleDragCancel = useCallback(() => setDisallowedSectionId(null), []);

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleSave = () => {
    if (saving) {
      return;
    }
    // Defensive backstop — canMove + schema refinements should prevent this.
    if (
      wouldCreateCycleForContainers({
        fieldConfigs,
        sectionConfigs,
        containers,
      })
    ) {
      return;
    }
    const output: FormEditorOutput = {
      sections: containerOrder.map((id) => ({
        id,
        name: sectionNames[id] ?? id,
        fieldIds: containers[id] ?? [],
        config: sectionConfigs[id],
      })),
      fields: Object.fromEntries(
        Object.entries(fields).map(([id, f]) => [
          id,
          { ...f, config: fieldConfigs[id] },
        ])
      ),
    };
    onSave(output);
  };

  const handleSectionSubmit = (data: SectionEditorValues) => {
    const sectionId = editingSectionId ?? generateId('section');

    // Keep `conditionalLogicEnabled` and `conditionalLogicRules` in sync.
    const noRules = !data.conditionalLogicRules?.length;
    const sanitized: SectionEditorValues =
      !data.conditionalLogicEnabled || noRules
        ? {
            ...data,
            conditionalLogicEnabled: false,
            conditionalLogicRules: [],
          }
        : data;

    if (!editingSectionId) {
      setContainerOrder((prev) => [...prev, sectionId]);
      setContainers((prev) => ({ ...prev, [sectionId]: [] }));
    }

    setSectionNames((prev) => ({ ...prev, [sectionId]: sanitized.name }));
    setSectionConfigs((prev) => ({ ...prev, [sectionId]: sanitized }));
    returnToFormEditor();
  };

  const handleFieldSubmit = (data: FieldEditorValues) => {
    const fieldId = editingFieldId ?? generateId('field');
    const existingField = fields[fieldId];

    const noRules = !data.conditionalLogicRules?.length;
    const sanitizedRules: FieldEditorValues =
      !data.conditionalLogicEnabled || noRules
        ? {
            ...data,
            conditionalLogicEnabled: false,
            // Preserve locked default rows on the clear path — they are
            // code-resident and must survive round-trips (AC#5). Today the
            // enable toggle is locked-on in merge mode so this path is
            // unreachable with locked rules, but keep the guard so a future
            // programmatic toggle-off can't silently wipe the default.
            conditionalLogicRules:
              data.conditionalLogicRules?.filter((r) => r.locked) ?? [],
          }
        : data;

    // Saving the field editor materialises an editable default into a real
    // customer config — clear the "still the unedited default" marker so it
    // persists from now on. A read-only default stays locked and unpersisted.
    const sanitized: FieldEditorValues = {
      ...sanitizedRules,
      conditionalLogicIsDefault: false,
    };

    setFields((prev) => ({
      ...prev,
      [fieldId]: {
        name: sanitized.fieldName,
        type: sanitized.fieldType,
        required: sanitized.required ?? false,
        readOnly: sanitized.readOnly ?? false,
        hidden: sanitized.hidden ?? false,
        // A field materialised through the field editor is always a custom
        // field — only custom fields can be added here. Without this default a
        // newly-added field round-tripped as `isCustom: undefined`, so
        // re-opening it read as a *standard* field and locked its type (and
        // options) with no explanation. Standard fields always come from
        // `initialData`, so they always have an `existingField` and keep their
        // own flag. Mirrors `editingFieldAllowsTarget`'s `?? true` below.
        isCustom: existingField ? existingField.isCustom : true,
        forcedRequired: existingField?.forcedRequired,
        canEditLabel: existingField?.canEditLabel,
        canEditOptions: existingField?.canEditOptions,
        canDelete: existingField?.canDelete,
        subtitle: existingField?.subtitle,
        isGroup: existingField?.isGroup,
        originalLabel: existingField?.originalLabel,
      },
    }));
    setFieldConfigs((prev) => ({ ...prev, [fieldId]: sanitized }));

    if (!editingFieldId && addingFieldToSectionId) {
      setContainers((prev) => ({
        ...prev,
        [addingFieldToSectionId]: [
          ...(prev[addingFieldToSectionId] ?? []),
          fieldId,
        ],
      }));
    }

    returnToFormEditor();
  };

  const [pendingDeletion, setPendingDeletion] = useState<DeletionImpact | null>(
    null
  );

  const removeConditionalLogicReferences = (deletedFieldIds: string[]) => {
    setFieldConfigs((prev) =>
      pruneRuleReferencesToFields(prev, deletedFieldIds)
    );
    setSectionConfigs((prev) =>
      pruneRuleReferencesToFields(prev, deletedFieldIds)
    );
  };

  const performDeleteField = (sectionId: string, fieldId: string) => {
    setContainers((prev) => ({
      ...prev,
      [sectionId]: prev[sectionId].filter((id) => id !== fieldId),
    }));
    setFields((prev) => {
      const next = { ...prev };
      delete next[fieldId];

      return next;
    });
    setFieldConfigs((prev) => {
      const next = { ...prev };
      delete next[fieldId];

      return next;
    });
    removeConditionalLogicReferences([fieldId]);
  };

  const performDeleteSection = (sectionId: string) => {
    const orphanedFieldIds = containers[sectionId] ?? [];

    setContainerOrder((prev) => prev.filter((id) => id !== sectionId));
    setContainers((prev) => {
      const next = { ...prev };
      delete next[sectionId];

      return next;
    });
    setSectionNames((prev) => {
      const next = { ...prev };
      delete next[sectionId];

      return next;
    });
    setSectionConfigs((prev) => {
      const next = { ...prev };
      delete next[sectionId];

      return next;
    });
    // Clean up orphaned fields that belonged to this section
    if (orphanedFieldIds.length > 0) {
      setFields((prev) => {
        const next = { ...prev };
        orphanedFieldIds.forEach((id) => delete next[id]);

        return next;
      });
      setFieldConfigs((prev) => {
        const next = { ...prev };
        orphanedFieldIds.forEach((id) => delete next[id]);

        return next;
      });
      removeConditionalLogicReferences(orphanedFieldIds);
    }
  };

  const getReferenceLabel = useCallback(
    (kind: 'field' | 'section', id: string): string => {
      if (kind === 'field') {
        return fields[id]?.name ?? id;
      }

      return sectionNames[id] ?? id;
    },
    [fields, sectionNames]
  );

  const handleDeleteField = (sectionId: string, fieldId: string) => {
    const fieldOwnRuleCount =
      fieldConfigs[fieldId]?.conditionalLogicRules?.length ?? 0;

    // External references = rules on other entities that reference this
    // field. The field's own rules are tracked separately so we can warn
    // about discarding them too, matching the section delete flow.
    const references = findRuleReferencesToFields({
      deletedFieldIds: [fieldId],
      fieldConfigs,
      sectionConfigs,
      getEntityLabel: getReferenceLabel,
      excludeFieldOwnerIds: new Set([fieldId]),
    });

    if (references.length === 0 && fieldOwnRuleCount === 0) {
      performDeleteField(sectionId, fieldId);

      return;
    }

    setPendingDeletion({
      kind: 'field',
      fieldId,
      fieldLabel: fields[fieldId]?.name ?? fieldId,
      references,
      fieldOwnRuleCount,
    });
  };

  const handleDeleteSection = (sectionId: string) => {
    const insideFieldIds = containers[sectionId] ?? [];
    const sectionConfig = sectionConfigs[sectionId];
    const sectionOwnRuleCount =
      sectionConfig?.conditionalLogicRules?.length ?? 0;

    // External references = rules on entities outside the deleted section
    // that reference fields inside it. The section's own rules and rules
    // on fields inside the section are excluded — they're being removed
    // alongside the section, not pruned from surviving state.
    const externalReferences = findRuleReferencesToFields({
      deletedFieldIds: insideFieldIds,
      fieldConfigs,
      sectionConfigs,
      getEntityLabel: getReferenceLabel,
      excludeFieldOwnerIds: new Set(insideFieldIds),
      excludeSectionOwnerIds: new Set([sectionId]),
    });

    if (
      insideFieldIds.length === 0 &&
      externalReferences.length === 0 &&
      sectionOwnRuleCount === 0
    ) {
      performDeleteSection(sectionId);

      return;
    }

    setPendingDeletion({
      kind: 'section',
      sectionId,
      sectionLabel: sectionNames[sectionId] ?? sectionId,
      insideFields: insideFieldIds.map((id) => ({
        id,
        label: getReferenceLabel('field', id),
      })),
      externalReferences,
      sectionOwnRuleCount,
    });
  };

  const handleConfirmDeletion = () => {
    if (!pendingDeletion) {
      return;
    }
    if (pendingDeletion.kind === 'field') {
      const sectionId = Object.keys(containers).find((id) =>
        containers[id].includes(pendingDeletion.fieldId)
      );
      if (sectionId) {
        performDeleteField(sectionId, pendingDeletion.fieldId);
      }
    } else {
      performDeleteSection(pendingDeletion.sectionId);
    }
    setPendingDeletion(null);
  };

  // Merge lang with defaults
  const formEditorLang = { ...DEFAULT_LANG.formEditor, ...lang?.formEditor };
  const sectionBuilderLang = {
    ...DEFAULT_LANG.sectionBuilder,
    ...lang?.sectionBuilder,
  };
  const fieldEditorLang = {
    ...DEFAULT_LANG.fieldEditor,
    ...lang?.fieldEditor,
  };

  const externalFieldMetaById = useMemo(() => {
    const map = new Map<string, ExternalFieldMeta>();
    for (const opt of externalFieldOptions ?? []) {
      map.set(opt.value, {
        allowAsSource: opt.allowAsSource,
        allowAsTarget: opt.allowAsTarget,
        freeText: opt.freeText,
        operatorOptions: opt.operatorOptions,
      });
    }

    return map;
  }, [externalFieldOptions]);

  const fieldOptions: ConditionalLogicOption[] = useMemo(
    () =>
      buildConditionalLogicFieldOptions({
        fields,
        fieldConfigs,
        externalFieldMetaById,
        getFieldTypeMetadata,
      }),
    [fields, fieldConfigs, externalFieldMetaById, getFieldTypeMetadata]
  );

  // Precedence: in-session user-typed options → host-supplied values →
  // per-type defaults from server data.
  const resolvedGetValueOptions = useCallback(
    (fieldId: string): ConditionalLogicOption[] => {
      const config = fieldConfigs[fieldId];
      if (config?.options?.length) {
        return config.options.map((opt) => ({
          value: opt.id,
          label: opt.label,
        }));
      }

      const hostResult = getValueOptions?.(fieldId);
      if (hostResult && hostResult.length > 0) {
        return hostResult;
      }

      const f = fields[fieldId];
      if (f) {
        const meta = getFieldTypeMetadata?.(f.type);
        if (meta?.defaultValueOptions) {
          return meta.defaultValueOptions;
        }
      }

      return [];
    },
    [fieldConfigs, getValueOptions, fields, getFieldTypeMetadata]
  );

  const editingField = editingFieldId ? fields[editingFieldId] : undefined;
  const isStandardField = editingField != null && !editingField.isCustom;
  // A custom field that has already been persisted (present in the initial,
  // loaded-from-DB snapshot) must not have its type changed — existing stored
  // values would no longer match the new type. Fields added earlier in this
  // unsaved session aren't in the snapshot, so they stay type-editable until
  // the form is saved and reopened. See CUS-199.
  const editingFieldIsPersistedCustom = isPersistedCustomField(
    editingFieldId,
    editingField,
    initialSnapshotRef.current?.fields
  );
  const canEditOptions = editingField?.canEditOptions !== false;
  const canEditLabel = editingField?.canEditLabel !== false;
  const editingFieldOriginalLabel = editingField?.originalLabel;
  // A read-only (strict-replace) code-declared default condition fully locks
  // the conditional-logic section in the field editor.
  const editingFieldConditionLocked = editingFieldId
    ? (fieldConfigs[editingFieldId]?.conditionalLogicLocked ?? false)
    : false;
  // Merge-mode defaults seed per-row locked rules instead. When any are
  // present the enable toggle is disabled (can't turn the section off) but the
  // rule builder stays interactive so the admin can add their own rules.
  const editingFieldHasLockedRules = editingFieldId
    ? (fieldConfigs[editingFieldId]?.conditionalLogicRules?.some(
        (r) => r.locked
      ) ?? false)
    : false;

  // Locking the type prevents a type swap from orphaning a rule's stored
  // values. Disabled rules count too — their values would still break on
  // re-enable.
  const editingFieldIsRuleSource = useMemo(() => {
    if (!editingFieldId) {
      return false;
    }
    const matchesRule = (rules?: { ifField: string }[]) =>
      rules?.some((r) => r.ifField === editingFieldId) ?? false;
    for (const config of Object.values(fieldConfigs)) {
      if (matchesRule(config.conditionalLogicRules)) {
        return true;
      }
    }
    for (const config of Object.values(sectionConfigs)) {
      if (matchesRule(config.conditionalLogicRules)) {
        return true;
      }
    }

    return false;
  }, [editingFieldId, fieldConfigs, sectionConfigs]);

  // Standard fields opt out of being a rule target via the registry.
  // Custom fields (incl. in-session) always allow it.
  const editingFieldAllowsTarget = editingFieldId
    ? (externalFieldMetaById.get(editingFieldId)?.allowAsTarget ??
      editingField?.isCustom ??
      true)
    : true;

  // A section can't depend on a field inside itself — the source would be
  // hidden alongside the section it controls.
  const sectionFieldOptions = useMemo(() => {
    if (!editingSectionId) {
      return fieldOptions;
    }
    const fieldsInSection = new Set(containers[editingSectionId] ?? []);

    return fieldOptions.filter((opt) => !fieldsInSection.has(opt.value));
  }, [fieldOptions, editingSectionId, containers]);

  // Candidates that would create an immediate cycle if picked as a rule
  // source on the entity being edited. Pre-filters the dropdown so users
  // don't pick options that would only fail at submit. Skipped for new
  // entities (no inbound dependents → no cycle possible from one rule).
  const sectionDisallowedCycleCandidates = useMemo(() => {
    if (!editingSectionId) {
      return new Set<string>();
    }

    // Candidates are already narrowed by `sectionFieldOptions` (fields inside
    // the section are stripped). The cycle helper would also catch those via
    // implicit-edge detection — running on the narrowed set is just cheaper.
    return getDisallowedCycleCandidates({
      entityType: 'section',
      entityId: editingSectionId,
      candidateFieldIds: sectionFieldOptions.map((o) => o.value),
      fieldConfigs,
      sectionConfigs,
      containers,
    });
  }, [
    editingSectionId,
    sectionFieldOptions,
    fieldConfigs,
    sectionConfigs,
    containers,
  ]);

  const fieldDisallowedCycleCandidates = useMemo(() => {
    if (!editingFieldId) {
      return new Set<string>();
    }

    return getDisallowedCycleCandidates({
      entityType: 'field',
      entityId: editingFieldId,
      candidateFieldIds: Object.keys(fields).filter(
        (id) => id !== editingFieldId
      ),
      fieldConfigs,
      sectionConfigs,
      containers,
    });
  }, [editingFieldId, fields, fieldConfigs, sectionConfigs, containers]);

  // Memoised default values for sub-dialogs
  const sectionBuilderDefaults = useMemo(
    () => buildSectionDefaults(editingSectionId, sectionNames, sectionConfigs),
    [editingSectionId, sectionNames, sectionConfigs]
  );

  const fieldEditorDefaults = useMemo(
    () => buildFieldDefaults(editingFieldId, fields, fieldConfigs),
    [editingFieldId, fields, fieldConfigs]
  );

  // Schemas with cycle detection context
  // Maps a cycle-graph vertex id (`field:<id>` / `section:<id>`) to a
  // human-readable label for the cycle-error message.
  const getEntityLabel = useCallback(
    (vertexId: string): string => {
      if (vertexId.startsWith('field:')) {
        const id = vertexId.slice('field:'.length);

        return fields[id]?.name ?? id;
      }
      if (vertexId.startsWith('section:')) {
        const id = vertexId.slice('section:'.length);
        const name = sectionNames[id];

        return name ? `Section "${name}"` : id;
      }

      return vertexId;
    },
    [fields, sectionNames]
  );

  const sectionSchema = useMemo(
    () =>
      createSectionEditorSchema({
        entityType: 'section',
        entityId: editingSectionId ?? generateId('section'),
        fieldConfigs,
        sectionConfigs,
        containers,
        getEntityLabel,
      }),
    [
      editingSectionId,
      fieldConfigs,
      sectionConfigs,
      containers,
      generateId,
      getEntityLabel,
    ]
  );

  // Single source of truth for the type lock: drives both the disabled select
  // and the schema. When the type is locked, neither it nor the option list is
  // user-editable, so the schema must not require either (both would be
  // unsatisfiable and block saving) — see createFieldEditorSchema.
  const isFieldTypeDisabled =
    isStandardField ||
    editingFieldIsRuleSource ||
    editingFieldIsPersistedCustom;
  const isFieldTypeEditable = !isFieldTypeDisabled;

  const fieldSchema = useMemo(
    () =>
      createFieldEditorSchema(
        {
          entityType: 'field',
          entityId: editingFieldId ?? generateId('field'),
          fieldConfigs,
          sectionConfigs,
          containers,
          getEntityLabel,
        },
        isFieldTypeEditable
      ),
    [
      editingFieldId,
      fieldConfigs,
      sectionConfigs,
      containers,
      generateId,
      getEntityLabel,
      isFieldTypeEditable,
    ]
  );

  return (
    <>
      <FormEditorMainDialog
        open={open && activeView === 'form-editor'}
        isDirty={isDirty}
        onClose={handleClose}
        onSave={handleSave}
        saving={saving}
        lang={formEditorLang}
        containerOrder={containerOrder}
        containers={containers}
        fields={fields}
        sectionNames={sectionNames}
        fieldConfigs={fieldConfigs}
        sectionConfigs={sectionConfigs}
        renderPreview={renderPreview}
        fieldTypeOptions={fieldTypeOptions}
        disallowedSectionId={disallowedSectionId}
        onMove={handleMove}
        canMove={canMove}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        onReorderContainers={(ids) => setContainerOrder(ids.map(String))}
        onEditField={(fieldId) => {
          setEditingFieldId(fieldId);
          navigateToSubEditor('field-editor');
        }}
        onDeleteField={handleDeleteField}
        onEditSection={(sectionId) => {
          setEditingSectionId(sectionId);
          navigateToSubEditor('section-builder');
        }}
        onDeleteSection={handleDeleteSection}
        onAddField={(sectionId) => {
          setEditingFieldId(null);
          setAddingFieldToSectionId(sectionId);
          navigateToSubEditor('field-editor');
        }}
        onAddSection={() => {
          setEditingSectionId(null);
          navigateToSubEditor('section-builder');
        }}
      />
      <SubEditorDialog
        open={open && activeView === 'section-builder'}
        onCancel={returnToFormEditor}
        title={sectionBuilderLang.title}
        saveLabel={
          editingSectionId
            ? sectionBuilderLang.editSave
            : sectionBuilderLang.addSave
        }
        cancelLabel={sectionBuilderLang.cancel}
        schema={sectionSchema as ZodType<SectionEditorValues>}
        defaultValues={sectionBuilderDefaults}
        onSubmit={handleSectionSubmit}
        initialOpenSection={'section-config'}
        configCard={
          <Card className={cn('w-full shadow-none')}>
            <CardHeader className={cn('border-b border-neutral-border')}>
              <CardTitle className={'text-lg font-bold'}>
                {'Section configuration'}
              </CardTitle>
            </CardHeader>
            <CardContent className={cn('pt-0')}>
              <FormTextField
                name={'name'}
                label={'Section name'}
                placeholder={'Enter section name'}
              />
            </CardContent>
          </Card>
        }
        fieldOptions={sectionFieldOptions}
        getValueOptions={resolvedGetValueOptions}
        currentEditingId={editingSectionId ?? undefined}
        disallowedCycleCandidates={sectionDisallowedCycleCandidates}
        conditionalLogicDescription={
          'Show this section based on the output from another field'
        }
        showGuidance={false}
      />
      <SubEditorDialog
        open={open && activeView === 'field-editor'}
        onCancel={returnToFormEditor}
        title={fieldEditorLang.title}
        saveLabel={
          editingFieldId ? fieldEditorLang.editSave : fieldEditorLang.addSave
        }
        cancelLabel={fieldEditorLang.cancel}
        schema={fieldSchema as ZodType<FieldEditorValues>}
        defaultValues={fieldEditorDefaults}
        onSubmit={handleFieldSubmit}
        initialOpenSection={'field-config'}
        configCard={
          <Card className={cn('w-full shadow-none')}>
            <CardHeader className={cn('border-b border-neutral-border')}>
              <CardTitle className={'text-lg font-bold'}>
                {'Field configuration'}
              </CardTitle>
              <CardAction>
                <div className={cn('flex items-center gap-4')}>
                  <ExclusiveSwitchGroup
                    options={[
                      { name: 'required', label: 'Required' },
                      { name: 'readOnly', label: 'Read only' },
                      { name: 'hidden', label: 'Hidden' },
                    ]}
                    disabled={!canEditOptions}
                  />
                </div>
              </CardAction>
            </CardHeader>
            <CardContent className={cn('pt-0')}>
              <FieldEditorConfigFields
                disableFieldType={isFieldTypeDisabled}
                disableFieldName={!canEditLabel}
                fieldTypeOptions={fieldTypeOptions ?? []}
                fieldTypeDisabledReason={
                  !isStandardField && editingFieldIsRuleSource
                    ? "This field's type is locked because it's used as a source in a conditional logic rule. Remove the rule first to change its type."
                    : editingFieldIsPersistedCustom
                      ? "The field type can't be changed once a field has been saved, to protect data already recorded against it. Create a new field to use a different type."
                      : undefined
                }
                originalLabel={editingFieldOriginalLabel}
              />
            </CardContent>
          </Card>
        }
        fieldOptions={fieldOptions}
        getValueOptions={resolvedGetValueOptions}
        currentEditingId={editingFieldId ?? undefined}
        disallowedCycleCandidates={fieldDisallowedCycleCandidates}
        conditionalLogicDescription={
          'Show this field based on the output from another field'
        }
        guidancePlaceholder={'Add guidance text...'}
        showConditionalLogic={editingFieldAllowsTarget}
        conditionalLogicLocked={editingFieldConditionLocked}
        conditionalLogicToggleLocked={editingFieldHasLockedRules}
      />
      <DeleteImpactConfirmationDialog
        impact={pendingDeletion}
        onCancel={() => setPendingDeletion(null)}
        onConfirm={handleConfirmDeletion}
      />
    </>
  );
};

export { FormEditorDialog };
